import { Dispatch, SetStateAction, useState } from "react";

import { MUNICIPAL_PROPERTY_SEARCH_DEBOUNCE_MS } from "@/domain/municipal-property/models/municipal-property-search-debounce-ms";
import { useDebounce } from "@/hooks/use-debounce";

interface DebouncedSearchFilter<TFilter extends { search?: string }> {
  filter: TFilter;
  setFilter: Dispatch<SetStateAction<TFilter>>;
  displayedFilter: TFilter;
  handleSearchChange: (search: string) => void;
}

export function useDebouncedSearchFilter<TFilter extends { search?: string }>(
  initialFilter: TFilter,
): DebouncedSearchFilter<TFilter> {
  const [filter, setFilter]: [TFilter, Dispatch<SetStateAction<TFilter>>] = useState<TFilter>(initialFilter);
  const [searchDraft, setSearchDraft]: [string, Dispatch<SetStateAction<string>>] = useState(initialFilter.search ?? "");
  const applySearch: (search: string) => void = useDebounce<string>(
    (search: string | undefined): void => setFilter((current: TFilter): TFilter => ({ ...current, search: search ?? "" })),
    MUNICIPAL_PROPERTY_SEARCH_DEBOUNCE_MS,
  );

  const handleSearchChange = (search: string): void => {
    setSearchDraft(search);
    applySearch(search);
  };

  return { filter, setFilter, displayedFilter: { ...filter, search: searchDraft }, handleSearchChange };
}
